/**
 * Who is signed in, and which of A1's states the console is in — SPEC §3.2.
 *
 * Nothing here is a token. The cookie is `HttpOnly` and the browser cannot read it; all this holds
 * is the answer `GET /api/auth` gave and the sentence that came with it. **Every 401 body is a
 * state**, not an error, and its sentence is shown above the form.
 */

import { create } from "zustand";
import { asSignInState, currentSession, type SignInState, type Who } from "../api/auth";
import { Failed } from "../api/client";

interface SessionState {
  /** `null` while the first `GET /auth` is in flight: neither signed in nor bounced yet. */
  who: Who | null;
  checked: boolean;
  state: SignInState;
  /** The server's sentence, verbatim. A1 *session ended*'s — "Nothing on the server stopped: the
   *  watcher and the funds kept running." — is the server's, and the console does not paraphrase
   *  it. */
  says: string;
  check: () => Promise<void>;
  /** A 401 on any request or on the stream lands here, in the matching state. */
  bounced: (state: string, says: string) => void;
  signedIn: (who: Who) => void;
  cleared: () => void;
  verifying: () => void;
  failed: (says: string) => void;
}

export const useSession = create<SessionState>((set) => ({
  who: null,
  checked: false,
  state: "idle",
  says: "",

  check: async () => {
    try {
      const answer = await currentSession();
      if (answer.signedIn) set({ who: answer.who, checked: true, state: "idle", says: "" });
      else set({ who: null, checked: true, state: answer.state, says: answer.says });
    } catch (error) {
      // The server did not answer at all. That is not a sign-in state: the strip says so, and the
      // console does not bounce someone to /signin because a GET timed out.
      if (error instanceof Failed && error.trouble.kind === "unreachable") {
        set({ checked: true });
        return;
      }
      throw error;
    }
  },

  bounced: (state, says) =>
    set({ who: null, checked: true, state: asSignInState(state), says }),

  signedIn: (who) => set({ who, checked: true, state: "idle", says: "" }),
  cleared: () => set({ who: null, checked: true, state: "idle", says: "" }),
  verifying: () => set({ state: "verifying", says: "" }),
  failed: (says) => set({ who: null, state: "sign-in failed", says }),
}));
